export type HandOutcome = 'win' | 'fold' | 'lost' | null;

type Args = {
  gameID: string;
  handNo: string;
  isWinner: boolean;
  isFolded: boolean;
  isShowdownLoss: boolean;
  isHandLive: boolean;
  isHandIdle?: boolean;
  cards: unknown;
};

type Result = {
  /** Latched for the rest of the hand, so it cannot be undone by a late tick. */
  outcome: HandOutcome;
  /** The hand as it stood when the outcome landed. */
  cardsAtResolve: unknown;
};

type Entry = { hand: string; outcome: HandOutcome; cards: unknown; lastCards: unknown };

/**
 * Latched outcomes, keyed by TABLE — module scope on purpose.
 *
 * This started as a `useRef` inside the tile, which was wrong for a reason that
 * only shows up in single-table view: the strip is rendered by the ACTIVE
 * panel's header into the shared fixed-chrome host, so switching tables hands
 * that host to a different React subtree and the whole strip remounts. Switch
 * rapidly and every remount wiped the latch, so each tile fell back to reading
 * the live fields and showed whatever half-resolved state they happened to be
 * in. The grid renders its header once and never remounts, which is exactly why
 * 2/3/4-up was unaffected while single-table view was a mess.
 *
 * An outcome belongs to a table's hand, not to a component instance, so it
 * lives where the table does. Same shape as utils/attentionCues and
 * utils/tableSnapState, which are module-level for the same reason.
 */
const outcomes = new Map<string, Entry>();

/** Bounded: a player holds four tables, but a long session opens and closes many. */
const MAX_TRACKED = 32;

/** Test-only. */
export const __resetHandOutcomes = (): void => outcomes.clear();

/**
 * Pin a hand's outcome to the hand it belongs to.
 *
 * The engine does not clear the fields describing a hand's ending together:
 * after a win, `pots`/`showdown` stop saying "winner" a beat before the seat
 * action stops saying "allin", and the card payload can repopulate on the way
 * out. Rendering each broadcast as it arrives played the resolution as a
 * sequence of half-states — WINNER, then ALL IN again, then the cards flashing
 * back — when the only thing that happened was "you won this hand".
 *
 * So the outcome is decided ONCE per hand and held. Returning to waiting or
 * cleanup clears it even if no next hand starts. A changed hand number or a
 * hand being live again also clears it (gameNo is not guaranteed
 * populated on every table's slice, and a latch that never clears leaves the
 * tile frozen on an old ending).
 *
 * A win overwrites anything already latched — you can be a fold AND a win in one
 * hand (you fold, everyone else folds too, you take it), and the engine can
 * report the hand over a beat before it reports who took it. Everything else
 * latches once and stays. 'lost' is the quiet one: dealt in, did not fold,
 * showdown went elsewhere, and it carries no badge on the tile.
 */
export const useHandOutcome = ({
  gameID,
  handNo,
  isWinner,
  isFolded,
  isShowdownLoss,
  isHandLive,
  isHandIdle,
  cards
}: Args): Result => {
  // A lone seated player may wait indefinitely without gameNo changing.
  // Clear before considering stale fold/winner/card fields on that snapshot.
  if (isHandIdle) {
    outcomes.delete(gameID);
    return { outcome: null, cardsAtResolve: null };
  }
  let entry = outcomes.get(gameID);

  if (!entry || entry.hand !== handNo || (isHandLive && entry.outcome !== null)) {
    entry = { hand: handNo, outcome: null, cards: null, lastCards: null };
    if (outcomes.size >= MAX_TRACKED) {
      const oldest = outcomes.keys().next().value;
      if (oldest !== undefined) outcomes.delete(oldest);
    }
    outcomes.set(gameID, entry);
  }

  // Remember the last hand we actually saw, not whatever `cards` happened to be
  // on the tick the outcome landed. Freezing the instant value showed face-down
  // backs for a whole hand whenever the outcome arrived on a cards-null tick.
  if (cards !== null) entry.lastCards = cards;

  // FOLD before LOST: both mean "out of this hand", and the fold is the more
  // specific thing to say about how.
  const candidate: HandOutcome = isWinner
    ? 'win'
    : isFolded
    ? 'fold'
    : isShowdownLoss
    ? 'lost'
    : null;

  if (candidate === 'win' && entry.outcome !== 'win') {
    entry.outcome = 'win';
    entry.cards = entry.lastCards;
  } else if (candidate !== null && entry.outcome === null) {
    entry.outcome = candidate;
    entry.cards = entry.lastCards;
  }

  return {
    outcome: entry.outcome,
    // Fall back to live cards rather than painting backs: a frozen null means we
    // never saw a hand, not that the player is holding nothing.
    cardsAtResolve: entry.outcome ? entry.cards ?? cards : cards
  };
};
